import { twJoin } from 'tailwind-merge';

import type { HeaderItemTone, HeaderMenuItem } from '@/components/headerItems/types';
import { Icon } from '@/components/ui/icon';
import { Menu, MenuItem, MenuItemLabel } from '@/components/ui/menu';
import { Pressable } from '@/components/ui/pressable';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type HeaderMenuButtonProps = {
  item: HeaderMenuItem;
  tone?: HeaderItemTone;
};

/**
 * A header menu where the bar is not UIKit's: one overflow control the size of the other
 * header actions, opening the item's actions beneath it. iOS shows the same item as the
 * system's own pull-down menu.
 *
 * A hidden action is left out here, since this menu is rebuilt each time it opens and has no
 * native shape to keep steady.
 */
export function HeaderMenuButton({ item, tone = 'default' }: HeaderMenuButtonProps) {
  const visibleActions = item.actions.filter((action) => !action.isHidden);

  return (
    <Menu
      placement="bottom right"
      offset={5}
      disabledKeys={visibleActions
        .filter((action) => action.isDisabled)
        .map((action) => action.key)}
      trigger={(triggerProps: Record<string, unknown>) => (
        <Pressable
          {...triggerProps}
          {...getAccessibilityProps({ label: item.label, role: 'button' })}
          disabled={item.isDisabled}
          accessibilityState={{ disabled: Boolean(item.isDisabled) }}
          className={twJoin(
            'h-12 w-12 items-center justify-center rounded-full web:cursor-pointer web:hover:bg-background-100',
            item.isDisabled && 'opacity-50'
          )}>
          <Icon
            as={item.icon.lucide}
            className={twJoin(
              'h-6 w-6',
              tone === 'onBrand' ? 'text-typography-white' : 'text-typography-900'
            )}
          />
        </Pressable>
      )}>
      {visibleActions.map((action) => (
        <MenuItem key={action.key} textValue={action.label} onPress={action.onPress}>
          <MenuItemLabel className={action.isDestructive ? 'text-error-700' : undefined}>
            {action.label}
          </MenuItemLabel>
        </MenuItem>
      ))}
    </Menu>
  );
}
